import { useCallback, useEffect, useState } from "react"

import type { ShareFilter } from "@/features/sharing/utils/share-filter"

import { isShareFilter } from "@/features/sharing/utils/share-filter"

/**
 * localStorage-persisted share filter ("All | Shared: With me · By me"),
 * remembered per storage key (convention: `sidebar-<feature>-share-filter-<sessionType>[-<scopeId>]`).
 * Re-reads storage when the key changes (e.g. switching session type or project scope).
 */
export function useStoredShareFilter(storageKey: string) {
  const [shareFilter, setShareFilterStored] = useState<ShareFilter>(() => {
    if (typeof window === "undefined") return "all"
    const stored = localStorage.getItem(storageKey)
    return isShareFilter(stored) ? stored : "all"
  })

  useEffect(() => {
    if (typeof window === "undefined") {
      setShareFilterStored("all")
      return
    }
    const stored = localStorage.getItem(storageKey)
    setShareFilterStored(isShareFilter(stored) ? stored : "all")
  }, [storageKey])

  const setShareFilter = useCallback(
    (filter: ShareFilter) => {
      setShareFilterStored(filter)
      if (typeof window !== "undefined") {
        localStorage.setItem(storageKey, filter)
      }
    },
    [storageKey]
  )

  return [shareFilter, setShareFilter] as const
}
